import React from "react";
import {useParams} from "react-router-dom";
import {CommunicationError, NotFound} from "../common/errors";
import {Box, Container, IconButton, Tooltip} from "@material-ui/core";
import {LibraryDTO} from "../../models/library";
import {API, createApiConfig} from "../../utils";
import {useKeycloak} from "@react-keycloak/web";
import {LoadingBackdrop} from "../common/Loading";
import {AxiosError, AxiosResponse} from "axios";
import {LibraryInfo} from "./browser/LibraryInfo";
import {LibraryBrowser} from "./browser/LibraryBrowser";
import ROUTES from "../../routes/routes";
import ListIcon from "@material-ui/icons/List";
import {useTranslation} from "react-i18next";
import BusinessIcon from "@material-ui/icons/Business";
import BuildIcon from "@material-ui/icons/Build";
import EditIcon from "@material-ui/icons/Edit";
import {BreadcrumbItem, BreadcrumbsRow} from "../common/breadcrumbs";
import LibraryAddIcon from "@material-ui/icons/LibraryAdd";
import {Link} from "react-router-dom";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";

interface LibraryDetailState {
    library: LibraryDTO | null;
    name: string;
}

interface LibraryParams {
    libraryId: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            display: "flex",
            alignItems: "flex-start",
            marginBottom: theme.spacing(3),
        },
        title: {
            flexGrow: 1,
            margin: 0,
            fontSize: "1.75rem",
            fontWeight: 700,
            color: "#111827",
        },
        iconGroup: {
            display: "flex",
            gap: theme.spacing(1),
            flexShrink: 0,
            marginLeft: theme.spacing(2),
        },
        iconBtn: {
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            color: "#374151",
            padding: "6px",
            "&:hover": {
                backgroundColor: "#F3F4F6",
                borderColor: "#D1D5DB",
            },
        },
        sectionTitle: {
            fontSize: "1.25rem",
            fontWeight: 700,
            color: "#111827",
            margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
        },
    })
);

interface IconNavButtonProps {
    title: string;
    to: string;
    children: React.ReactNode;
    className: string;
}

const IconNavButton: React.FC<IconNavButtonProps> = ({title, to, children, className}) => (
    <Tooltip title={title}>
        <IconButton className={className} size="small" component={Link} to={to}>
            {children}
        </IconButton>
    </Tooltip>
);

export const LibraryDetail: React.FC = () => {
    const [t] = useTranslation("libraries");
    const {libraryId} = useParams<LibraryParams>();
    const {keycloak, initialized} = useKeycloak();
    const classes = useStyles();
    const initState: LibraryDetailState = {
        library: null,
        name: "loading",
    };
    const [state, setState] = React.useState(initState);
    React.useEffect(() => {
        if (state.name === "loading") {
            API.get<LibraryDTO>(`/libraries/${libraryId}`, createApiConfig(keycloak, initialized))
                .then((res: AxiosResponse<LibraryDTO>) => {
                    setState({...state, library: res.data, name: "loaded"});
                })
                .catch((err: AxiosError) => {
                    if (err?.response?.status === 404) {
                        setState({...state, name: "not_found"});
                    } else {
                        setState({...state, name: "failed"});
                    }
                });
        }
    }, [state, setState, libraryId, keycloak, initialized]);
    if (state.name === "loading") {
        return <LoadingBackdrop />;
    }
    if (state.name === "not_found") {
        return <NotFound />;
    }
    if (state.name === "failed") {
        return <CommunicationError />;
    }
    if (state.library !== null) {
        return (
            <Container maxWidth="lg">
                <BreadcrumbsRow>
                    <BreadcrumbItem name={t("library.titles.libraries")} route={ROUTES.libraries.path} />
                    <BreadcrumbItem name={state.library.name} />
                </BreadcrumbsRow>
                <Box className={classes.header}>
                    <h1 className={classes.title}>{state.library.name}</h1>
                    <Box className={classes.iconGroup}>
                        <IconNavButton
                            title={t("library.actions.to_list")}
                            to={ROUTES.libraries.path}
                            className={classes.iconBtn}
                        >
                            <ListIcon fontSize="small" />
                        </IconNavButton>
                        <IconNavButton
                            title={t("library.actions.organization")}
                            to={ROUTES.organization.create(state.library.organization.publicId)}
                            className={classes.iconBtn}
                        >
                            <BusinessIcon fontSize="small" />
                        </IconNavButton>
                        <IconNavButton
                            title={t("library.actions.clone")}
                            to={ROUTES.libraryClone.create(state.library.id)}
                            className={classes.iconBtn}
                        >
                            <LibraryAddIcon fontSize="small" />
                        </IconNavButton>
                        <IconNavButton
                            title={t("library.actions.editor")}
                            to={ROUTES.libraryEditor.create(state.library.id)}
                            className={classes.iconBtn}
                        >
                            <BuildIcon fontSize="small" />
                        </IconNavButton>
                        <IconNavButton
                            title={t("library.actions.edit")}
                            to={ROUTES.libraryEdit.create(state.library.id)}
                            className={classes.iconBtn}
                        >
                            <EditIcon fontSize="small" />
                        </IconNavButton>
                    </Box>
                </Box>
                <LibraryInfo library={state.library} />
                <h2 className={classes.sectionTitle}>{t("library.titles.content")}</h2>
                <LibraryBrowser library={state.library} />
            </Container>
        );
    }
    return <CommunicationError />;
};

export default LibraryDetail;
